import { useMemo } from 'react';
import type { ComponentType, SVGProps } from 'react';

import { Toast as BaseToast } from '@base-ui/react/toast';
import {
  alertContentRecipe,
  alertDescriptionRecipe,
  alertDismissRecipe,
  alertIconRecipe,
  alertRecipe,
  alertTitleRecipe,
} from 'components/Alert';
import CheckCircleIcon from 'components/Alert/check-circle.svg?react';
import CloseIcon from 'components/Alert/close.svg?react';
import InfoIcon from 'components/Alert/info.svg?react';
import WarningCircleIcon from 'components/Alert/warning-circle.svg?react';
import WarningIcon from 'components/Alert/warning.svg?react';
import { Button, IconButton } from 'components/Button';
import { useInheritedDarkMode } from 'components/Theme';
import { cn } from 'components/utils';

import { toastActionsRecipe, toastRootRecipe, toastViewportRecipe } from './Toast.recipe';

import type { ToastOptions, ToastProviderProps, ToastSeverity, ToastViewportProps } from './types';

const ICONS: Record<ToastSeverity, ComponentType<SVGProps<SVGSVGElement>>> = {
  neutral: InfoIcon,
  info: InfoIcon,
  success: CheckCircleIcon,
  warning: WarningIcon,
  error: WarningCircleIcon,
};

const SEVERITIES = new Set<string>(Object.keys(ICONS));

const toSeverity = (type: string | undefined): ToastSeverity =>
  type && SEVERITIES.has(type) ? (type as ToastSeverity) : 'neutral';

const ACTION_TIMEOUT = 8000;

/** The Alert's politeness, so a warning or error interrupts and a confirmation waits its turn. */
const isUrgent = (severity: ToastSeverity) => severity === 'warning' || severity === 'error';

/**
 * Design System 2.0 toast provider. Wrap the app once; render one `ToastViewport` inside it.
 *
 * Base UI's `Toast` does the lifecycle: timers that pause while the stack is hovered or focused, or
 * the window is hidden, swipe to dismiss, the F6 shortcut into the region and the live-region
 * announcements. This layer only styles it and sets Natter's defaults.
 */
export const ToastProvider = ({ children, timeout = 5000, limit = 3 }: ToastProviderProps) => (
  <BaseToast.Provider limit={limit} timeout={timeout}>
    {children}
  </BaseToast.Provider>
);

/**
 * Adds and closes toasts. `add` returns the toast's id.
 *
 * Wraps Base UI's manager so the defaults live in one place rather than at every call site: an
 * error stays until closed, a toast with an action gets 8s, warnings and errors announce
 * assertively, and pressing the action also closes the toast.
 */
export const useToastManager = () => {
  const { add, close } = BaseToast.useToastManager();

  return useMemo(
    () => ({
      add: ({ description, severity = 'neutral', title, action, timeout, id }: ToastOptions) => {
        // Read by the action's handler, which only runs after `add` has returned.
        const toastId: string = add({
          id,
          title,
          description,
          type: severity,
          priority: isUrgent(severity) ? 'high' : 'low',
          timeout: timeout ?? (severity === 'error' ? 0 : action ? ACTION_TIMEOUT : undefined),
          actionProps: action
            ? {
                children: action.label,
                onClick: () => {
                  action.onClick();
                  close(toastId);
                },
              }
            : undefined,
        });

        return toastId;
      },
      close: (toastId: string) => close(toastId),
    }),
    [add, close]
  );
};

/**
 * The fixed region the toasts stack in: bottom-right, full width on a phone.
 *
 * Portalled to `<body>`, so the theme is carried across from where this is rendered — put it
 * inside the `.dark` region a dark surface wants its toasts to match.
 *
 * Wrap the app once in `ToastProvider` and render one `ToastViewport` inside the desired theme
 * region. A descendant calls `useToastManager().add(options)`; `description` is required. This is
 * the complete public usage path, without the private `Page` and `Fire` helpers in the stories:
 *
 * ```tsx
 * import { Button, ToastProvider, ToastViewport, useToastManager } from 'components';
 *
 * const SaveButton = () => {
 *   const toast = useToastManager();
 *   return (
 *     <Button appearance="secondary" onClick={() => toast.add({ description: 'Saved', severity: 'success' })}>
 *       Save
 *     </Button>
 *   );
 * };
 *
 * export const ToastExample = () => (
 *   <ToastProvider>
 *     <SaveButton />
 *     <ToastViewport />
 *   </ToastProvider>
 * );
 * ```
 *
 * `add` returns the toast id; `toast.close(id)` closes it programmatically. Pass the same `id` in
 * `add({ id, description })` to replace a repeated message rather than stack duplicates. Set one
 * toast's `timeout: 0` to keep it until dismissal without changing the provider's default timer.
 * Errors stay by default; an action gets 8 seconds. The close control is always rendered.
 */
export const ToastViewport = ({ className, closeLabel = 'Dismiss notification' }: ToastViewportProps) => {
  const { toasts } = BaseToast.useToastManager();
  const { sentinelRef, inheritedDarkClass } = useInheritedDarkMode(toasts.length > 0);

  return (
    <>
      <span ref={sentinelRef} hidden />
      <BaseToast.Portal className={inheritedDarkClass}>
        <BaseToast.Viewport className={cn(toastViewportRecipe(), className)}>
          {toasts.map((toast) => {
            const severity = toSeverity(toast.type);
            const Icon = ICONS[severity];

            return (
              <BaseToast.Root
                key={toast.id}
                className={cn(alertRecipe({ severity }), toastRootRecipe())}
                data-part="panel"
                data-severity={severity}
                swipeDirection="right"
                toast={toast}
              >
                <span aria-hidden className={alertIconRecipe()} data-part="icon">
                  <Icon aria-hidden="true" focusable="false" />
                </span>

                <div className={alertContentRecipe()}>
                  {toast.title && <BaseToast.Title className={alertTitleRecipe()} data-part="title" render=<p /> />}
                  <BaseToast.Description className={alertDescriptionRecipe()} data-part="description" />
                  {toast.actionProps && (
                    <div className={toastActionsRecipe()}>
                      <BaseToast.Action
                        render={
                          <Button appearance="secondary" data-part="action" size="xs">
                            {toast.actionProps.children}
                          </Button>
                        }
                      />
                    </div>
                  )}
                </div>

                <BaseToast.Close
                  render={
                    <IconButton
                      appearance="tertiary"
                      aria-label={closeLabel}
                      className={alertDismissRecipe()}
                      data-part="dismiss"
                      fill="default"
                      size="2xs"
                    >
                      <CloseIcon aria-hidden="true" focusable="false" />
                    </IconButton>
                  }
                />
              </BaseToast.Root>
            );
          })}
        </BaseToast.Viewport>
      </BaseToast.Portal>
    </>
  );
};
